import React, { useEffect, useState } from 'react';
import { authAPI } from '../services/api';

const AccountSettings = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [profilePassword, setProfilePassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await authAPI.getProfile();
        setUsername(response.data.user.username || '');
        setEmail(response.data.user.email || '');
      } catch (err) {
        setProfileError(err.response?.data?.error || 'Failed to load profile');
      } finally {
        setLoadingProfile(false);
      }
    };
    loadProfile();
  }, []);

  const persistSession = (data) => {
    if (data.token) {
      localStorage.setItem('token', data.token);
    }
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
      window.dispatchEvent(new Event('user-updated'));
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileMessage('');
    setSavingProfile(true);

    try {
      const response = await authAPI.updateProfile({
        username: username.trim(),
        email: email.trim(),
        currentPassword: profilePassword,
      });
      persistSession(response.data);
      setProfilePassword('');
      setProfileMessage(response.data.message || 'Profile updated successfully');
    } catch (err) {
      setProfileError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordMessage('');

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    setSavingPassword(true);

    try {
      const response = await authAPI.changePassword({
        currentPassword,
        newPassword,
      });
      persistSession(response.data);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMessage(response.data.message || 'Password updated successfully');
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loadingProfile) {
    return (
      <div className="main-content">
        <div className="page-header">
          <h1 className="page-title">Account Settings</h1>
          <p className="page-subtitle">Loading your account…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <h1 className="page-title">Account Settings</h1>
        <p className="page-subtitle">Update your username, email, and password</p>
      </div>

      <div style={styles.grid}>
        <div className="card" style={styles.card}>
          <div className="card-header">
            <h2 className="card-title">Profile</h2>
          </div>
          {profileError && <div style={styles.error}>{profileError}</div>}
          {profileMessage && <div style={styles.success}>{profileMessage}</div>}
          <form onSubmit={handleProfileSubmit} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                minLength={3}
                style={styles.input}
                autoComplete="username"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={styles.input}
                autoComplete="email"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Current password</label>
              <input
                type="password"
                value={profilePassword}
                onChange={(e) => setProfilePassword(e.target.value)}
                required
                style={styles.input}
                autoComplete="current-password"
                placeholder="Required to save profile changes"
              />
            </div>
            <button type="submit" disabled={savingProfile} style={styles.button}>
              {savingProfile ? 'Saving…' : 'Save profile'}
            </button>
          </form>
        </div>

        <div className="card" style={styles.card}>
          <div className="card-header">
            <h2 className="card-title">Password</h2>
          </div>
          {passwordError && <div style={styles.error}>{passwordError}</div>}
          {passwordMessage && <div style={styles.success}>{passwordMessage}</div>}
          <form onSubmit={handlePasswordSubmit} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Current password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                style={styles.input}
                autoComplete="current-password"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>New password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                style={styles.input}
                autoComplete="new-password"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Confirm new password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                style={styles.input}
                autoComplete="new-password"
              />
            </div>
            <button type="submit" disabled={savingPassword} style={styles.button}>
              {savingPassword ? 'Updating…' : 'Update password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const styles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '1.5rem',
  },
  card: {
    padding: '1.5rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  formGroup: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    color: '#374151',
    fontWeight: '500',
    fontSize: '0.875rem',
  },
  input: {
    width: '100%',
    padding: '0.875rem',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '0.9375rem',
    boxSizing: 'border-box',
  },
  button: {
    padding: '0.875rem 1.5rem',
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.9375rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '0.25rem',
  },
  error: {
    color: '#991b1b',
    marginBottom: '1rem',
    padding: '0.875rem',
    backgroundColor: '#fee2e2',
    borderRadius: '8px',
    border: '1px solid #fecaca',
    fontSize: '0.875rem',
  },
  success: {
    color: '#065f46',
    marginBottom: '1rem',
    padding: '0.875rem',
    backgroundColor: '#d1fae5',
    borderRadius: '8px',
    border: '1px solid #a7f3d0',
    fontSize: '0.875rem',
  },
};

export default AccountSettings;
